/**
 * Carrier MCP stdio — ui_agent_reply + ui_agent_list_pending.
 *
 * When a hosted run pauses to ask a question, `@carrier/ai` writes a pending-ask
 * entry under `manus_pending_ask:` — still that prefix two vendors later,
 * because live Worker KV entries sit under it and renaming orphans every one.
 *
 * TWO STORES, AND WHY BOTH
 * ------------------------
 * These tools read from `CARRIER_USERS` when that binding exists and fall back
 * to this process's session store when it does not. stdio stubs the binding to
 * null today, so the fallback is what actually runs — but the binding is one
 * config change away from being real, and `CARRIER_USERS` is one namespace
 * shared with the deployed Worker. The moment it appears, these tools are
 * reading the Worker's keys, written by and for other customers.
 *
 * That is exactly how five cross-tenant holes stayed latent in this package:
 * they were only safe because the binding was null, and they went live the
 * instant one appeared. So the ownership check below is NOT conditional on
 * which store is in use. It costs nothing against a private Map and is the only
 * thing standing between a real binding and handing out another tenant's
 * browser session.
 *
 * TENANCY
 * -------
 * `manus_pending_ask:*` is a flat prefix over every customer and the entry
 * carries no owner. Ownership comes from the `steel_task:<id>` record's
 * `owner_sub`, the same field the Worker's `ui_agent_status` checks. A task with
 * no `owner_sub` predates that field and is treated as nobody's, not
 * everybody's.
 *
 * WHAT THIS FIXES ALONG THE WAY
 * -----------------------------
 * `ui_agent_list_pending` used to call `ctx.env.CARRIER_USERS.list(...)` after a
 * scope check alone. On a null binding that threw a synchronous TypeError at the
 * caller instead of returning an error result — the failure mode
 * `stdio-null-kv.test.js` exists to catch, and one a chained `.catch()` does not
 * rescue. It is guarded now.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import type { ToolScope } from "./types.js";
import {
  PENDING_ASK_PREFIX,
  PENDING_ASK_TTL_SECONDS,
  resumeUiAgentTask,
  type SteelPendingAsk,
  type UiAgentEnv,
  type UiAgentStore,
} from "@carrier/ai";
import type { StdioUiAgentRuntime } from "./ui-agent-runtime.js";

/** Doc + registry merge — ui_agent_reply enforces write|admin; list_pending allows any non-empty scope. */
export const UI_AGENT_ASK_TOOL_SCOPES: Record<string, ToolScope> = {
  ui_agent_reply: "write",
  ui_agent_list_pending: "read",
};

type ToolResult = {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
};

interface PendingAskEntry extends SteelPendingAsk {
  expires_at: string; // ISO 8601 — computed from asked_at + TTL
}

function computeExpiresAt(askedAt: string): string {
  const asked = new Date(askedAt).getTime();
  if (isNaN(asked)) return "";
  return new Date(asked + PENDING_ASK_TTL_SECONDS * 1000).toISOString();
}

/** Used only when neither a binding nor a runtime is present. */
const emptyStore: UiAgentStore = {
  get: async () => null,
  put: async () => undefined,
  delete: async () => undefined,
};

/**
 * Whether this OAuth subject dispatched the task behind a pending ask.
 *
 * A task_id is not a secret, so it cannot stand in for authorisation. Read
 * failures and missing records both answer false: refusing an ask whose owner
 * cannot be established costs the caller a retry, while allowing it hands
 * another tenant's session away.
 *
 * The binding read stays inside try/catch because `ctx.env.CARRIER_USERS` is
 * null in stdio and `null.get(...)` throws synchronously — inside the try, so
 * it is caught, and the fallback store answers instead.
 */
async function callerOwnsTask(
  ctx: ToolContext,
  taskId: string,
  fallback: UiAgentStore | undefined,
): Promise<boolean> {
  const key = `steel_task:${taskId}`;
  let raw: string | null = null;
  try {
    raw = await ctx.env.CARRIER_USERS.get(key);
  } catch {
    if (!fallback) return false;
    try {
      raw = await fallback.get(key);
    } catch {
      return false;
    }
  }
  if (!raw) return false;
  try {
    const task = JSON.parse(raw) as { owner_sub?: string };
    return task.owner_sub !== undefined && task.owner_sub === ctx.props.sub;
  } catch {
    return false;
  }
}

/** KV-with-list, when a real binding is present. */
interface ListableStore extends UiAgentStore {
  list(opts: { prefix: string }): Promise<{ keys: Array<{ name: string }> }>;
}

function listableBinding(ctx: ToolContext): ListableStore | null {
  const kv = ctx.env.CARRIER_USERS as unknown as ListableStore | null;
  return kv && typeof kv.list === "function" ? kv : null;
}

export function registerUiAgentAskTools(
  server: McpServer,
  ctx: ToolContext,
  runtime?: StdioUiAgentRuntime,
): void {
  /**
   * The env handed to the shared module: the real binding when there is one,
   * otherwise this session's Map. `audit` degrades to an explicit no-op rather
   * than being omitted, because a caller with nowhere to write spend should say
   * so instead of losing the record silently.
   */
  const sharedEnv = (): UiAgentEnv => ({
    ...(runtime?.env.apiKey ? { apiKey: runtime.env.apiKey } : {}),
    store:
      (ctx.env.CARRIER_USERS as unknown as UiAgentStore | null) ??
      runtime?.env.store ??
      emptyStore,
    audit: runtime?.env.audit ?? (() => undefined),
  });

  // =========================================================================
  // ui_agent_reply — resume a paused task
  // =========================================================================
  server.registerTool(
    "ui_agent_reply",
    {
      title: "Reply to Paused UI Agent Task",
      description:
        "Resumes a browser automation task that paused to ask a question. " +
        "Use ui_agent_list_pending to find tasks waiting for input. " +
        "Provide the task_id and your reply (e.g. a 2FA code, a field value, or a yes/no answer). " +
        "The reply content is never recorded in audit logs — only its length is logged.",
      inputSchema: {
        task_id: z
          .string()
          .describe("Task ID to resume (from ui_agent_list_pending)"),
        reply: z
          .string()
          .describe(
            "Your answer to the agent's question (e.g. a 2FA code or confirmation)",
          ),
      },
    },
    async (args: { task_id: string; reply: string }): Promise<ToolResult> => {
      const start = Date.now();
      const { task_id, reply } = args;

      if (
        !ctx.props.scope.includes("write") &&
        !ctx.props.scope.includes("admin")
      ) {
        ctx.audit({
          tool_name: "ui_agent_reply",
          ocs_method: "[ui-agent:ask-reply]",
          status: "scope_denied",
          dry_run: false,
          duration_ms: 0,
        });
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: `Scope denied: 'ui_agent_reply' requires 'write' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
            },
          ],
        };
      }

      const env = sharedEnv();

      let pendingRaw: string | null = null;
      try {
        pendingRaw = await env.store.get(`${PENDING_ASK_PREFIX}${task_id}`);
      } catch {
        pendingRaw = null;
      }

      // Ownership is folded into the same not-pending answer on purpose: a
      // distinct "not yours" would let a caller probe for live task ids.
      if (
        pendingRaw === null ||
        !(await callerOwnsTask(ctx, task_id, runtime?.env.store))
      ) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: JSON.stringify({
                error: "task_not_pending",
                message:
                  `No pending-ask entry found for task_id=${task_id}. ` +
                  "The task may have already been resumed, completed, or expired (24h TTL). " +
                  "Use ui_agent_list_pending to see currently waiting tasks.",
              }),
            },
          ],
        };
      }

      const outcome = await resumeUiAgentTask(task_id, reply, env);

      // Length only, never the reply itself — it may carry a 2FA code.
      ctx.audit({
        tool_name: "ui_agent_reply",
        ocs_method: "[ui-agent:ask-reply]",
        status: outcome.ok ? "ui_agent_resumed" : "error",
        dry_run: false,
        duration_ms: Date.now() - start,
        event_type: "ui_agent_resume",
      });

      if (!outcome.ok) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: JSON.stringify({
                error: "resume_failed",
                message: outcome.error ?? "The task could not be resumed.",
              }),
            },
          ],
        };
      }

      return {
        content: [
          {
            type: "text" as const,
            text: JSON.stringify(
              {
                status: "ui_agent_resumed",
                task_id,
                reply_length: reply.length,
              },
              null,
              2,
            ),
          },
        ],
      };
    },
  );

  // =========================================================================
  // ui_agent_list_pending — list tasks waiting for input
  // =========================================================================
  server.registerTool(
    "ui_agent_list_pending",
    {
      title: "List Pending UI Agent Tasks (Waiting for Input)",
      description:
        "Returns browser automation tasks currently paused waiting for human input. " +
        "Shows the agent's question, task URL, and when it was asked. " +
        "Entries expire after 24 hours. Use ui_agent_reply to resume a task.",
      inputSchema: {},
    },
    async (): Promise<ToolResult> => {
      if (ctx.props.scope.length === 0) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: "Scope denied: no scopes present on token.",
            },
          ],
        };
      }

      // Two ways to enumerate. A real KV binding can scan the shared prefix —
      // which is why every hit then has to clear callerOwnsTask, since that
      // prefix spans every customer. A Map-backed store exposes no list() by
      // design (UiAgentStore is deliberately the three methods the shared
      // module needs), so the session's own dispatched ids stand in.
      const kv = listableBinding(ctx);
      let taskIds: string[];

      if (kv) {
        try {
          const listing = await kv.list({ prefix: PENDING_ASK_PREFIX });
          taskIds = listing.keys.map((k) =>
            k.name.slice(PENDING_ASK_PREFIX.length),
          );
        } catch (err) {
          return {
            isError: true,
            content: [
              {
                type: "text" as const,
                text: `Error listing pending tasks: ${err instanceof Error ? err.message : String(err)}`,
              },
            ],
          };
        }
      } else {
        taskIds = [...(runtime?.dispatchedTaskIds ?? [])];
      }

      const store = sharedEnv().store;
      const entries = await Promise.all(
        taskIds.map(async (taskId): Promise<PendingAskEntry | null> => {
          let raw: string | null;
          try {
            raw = await store.get(`${PENDING_ASK_PREFIX}${taskId}`);
          } catch {
            return null;
          }
          if (!raw) return null;
          if (!(await callerOwnsTask(ctx, taskId, runtime?.env.store))) {
            return null;
          }
          try {
            const parsed = JSON.parse(raw) as SteelPendingAsk;
            return { ...parsed, expires_at: computeExpiresAt(parsed.asked_at) };
          } catch {
            return null;
          }
        }),
      );

      const validEntries = entries.filter(
        (e): e is PendingAskEntry => e !== null,
      );

      return {
        content: [
          {
            type: "text" as const,
            text: JSON.stringify(
              { pending_tasks: validEntries, count: validEntries.length },
              null,
              2,
            ),
          },
        ],
      };
    },
  );
}
